import { useEffect, useMemo, useState } from 'react';
import { Linkedin, Search, Loader2, ExternalLink, Users, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MultiSelectPopover, type MultiSelectPopoverOption } from '@/shared/components/MultiSelectPopover';

const ACTIONS_API_BASE = '/linkedin-api/actions';

interface LinkedInPreset {
  name: string;
  keywords: string;
  locations: string[];
  degrees?: string[];
  industries?: string[];
}

interface LinkedInPresetsResponse {
  presets: LinkedInPreset[];
  locations: {
    countries: Record<string, string>;
    canada: Record<string, string>;
    us: Record<string, string>;
  };
  industries: Record<string, string>;
  degrees: Record<string, string>;
}

interface PeopleSearchContact {
  linkedinUrl: string;
  name: string;
  firstName: string;
  lastName: string;
  title: string;
  location: string;
}

interface PeopleSearchResponse {
  contacts: PeopleSearchContact[];
  totalResults: number;
  pagesSearched: number;
  stoppedReason: string;
  screenshot: string;
  url: string;
}

interface CompanySearchResult {
  linkedinUrl: string;
  name: string;
  industry: string;
  location: string;
  followers: string;
  description: string;
}

interface CompanySearchResponse {
  companies: CompanySearchResult[];
  pagesSearched: number;
  screenshot: string;
  url: string;
}

type SearchType = 'people' | 'companies';

async function actionsApiCall<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${ACTIONS_API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...options.headers } as Record<string, string>,
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || res.statusText);
  }
  return res.json() as Promise<T>;
}

function humanize(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function toOptions(map: Record<string, string>, group?: string): MultiSelectPopoverOption[] {
  return Object.entries(map).map(([key, code]) => ({
    id: code,
    label: humanize(key),
    secondaryLabel: group,
  }));
}

function describeStoppedReason(reason: string): string {
  switch (reason) {
    case 'rate_limit_exceeded':
      return 'LinkedIn rate-limited this search — try again in a few minutes.';
    case 'pagination_failed':
      return 'Search stopped early — try narrowing your filters.';
    default:
      return 'No results for these filters.';
  }
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

interface LinkedInAutomationTabProps {
  userId: string;
  isActive: boolean;
}

export function LinkedInAutomationTab({ userId, isActive }: LinkedInAutomationTabProps) {
  const [presets, setPresets] = useState<LinkedInPresetsResponse | null>(null);

  const [searchType, setSearchType] = useState<SearchType>('people');
  const [keywords, setKeywords] = useState('');
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedDegrees, setSelectedDegrees] = useState<string[]>([]);

  const [isSearching, setIsSearching] = useState(false);
  const [peopleResults, setPeopleResults] = useState<PeopleSearchResponse | null>(null);
  const [companyResults, setCompanyResults] = useState<CompanySearchResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    actionsApiCall<LinkedInPresetsResponse>('/search/presets')
      .then((data) => {
        if (!cancelled) setPresets(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          toast.error('Could not load search filters', { description: errorMessage(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const locationOptions = useMemo<MultiSelectPopoverOption[]>(() => {
    if (!presets) return [];
    return [
      ...toOptions(presets.locations.countries, 'Country'),
      ...toOptions(presets.locations.canada, 'Canada'),
      ...toOptions(presets.locations.us, 'US'),
    ];
  }, [presets]);

  const industryOptions = useMemo<MultiSelectPopoverOption[]>(
    () => (presets ? toOptions(presets.industries) : []),
    [presets],
  );

  const degreeOptions = useMemo<MultiSelectPopoverOption[]>(
    () => (presets ? toOptions(presets.degrees) : []),
    [presets],
  );

  const handleSearchTypeChange = (value: string) => {
    if (!value) return;
    setSearchType(value as SearchType);
    setPeopleResults(null);
    setCompanyResults(null);
  };

  const handleApplyPreset = (preset: LinkedInPreset) => {
    setKeywords(preset.keywords);
    setSelectedLocations(preset.locations);
    setSelectedDegrees(preset.degrees ?? []);
    setSelectedIndustries(preset.industries ?? []);
  };

  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!keywords.trim()) return;
    setIsSearching(true);
    try {
      if (searchType === 'people') {
        const data = await actionsApiCall<PeopleSearchResponse>('/search', {
          method: 'POST',
          headers: { 'x-user-id': userId },
          body: JSON.stringify({
            keywords: keywords.trim(),
            locations: selectedLocations.length ? selectedLocations : undefined,
            degrees: selectedDegrees.length ? selectedDegrees : undefined,
            industries: selectedIndustries.length ? selectedIndustries : undefined,
          }),
        });
        setPeopleResults(data);
      } else {
        const data = await actionsApiCall<CompanySearchResponse>('/companies/search', {
          method: 'POST',
          headers: { 'x-user-id': userId },
          body: JSON.stringify({
            keywords: keywords.trim(),
            locations: selectedLocations.length ? selectedLocations : undefined,
            industries: selectedIndustries.length ? selectedIndustries : undefined,
          }),
        });
        setCompanyResults(data);
      }
    } catch (err: unknown) {
      toast.error('Search failed', { description: errorMessage(err) });
    } finally {
      setIsSearching(false);
    }
  };

  if (!isActive) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <div className="text-center space-y-4">
          <Linkedin className="h-12 w-12 mx-auto opacity-30" />
          <p className="text-lg">Start a session first</p>
          <p className="text-sm">Use the "Start Session" button above to begin</p>
        </div>
      </div>
    );
  }

  const activeResults = searchType === 'people' ? peopleResults : companyResults;
  const hasSearched = activeResults !== null;

  return (
    <div className="flex flex-col h-full overflow-auto">
      <div className="px-6 py-4 border-b bg-white space-y-4">
        <ToggleGroup
          type="single"
          value={searchType}
          onValueChange={handleSearchTypeChange}
          className="justify-start rounded-md border bg-background p-1 w-fit"
        >
          <ToggleGroupItem
            value="people"
            size="sm"
            className="h-8 px-3 text-xs gap-1.5 data-[state=on]:bg-atek-blue data-[state=on]:text-white"
          >
            <Users className="h-3.5 w-3.5" />
            People
          </ToggleGroupItem>
          <ToggleGroupItem
            value="companies"
            size="sm"
            className="h-8 px-3 text-xs gap-1.5 data-[state=on]:bg-atek-blue data-[state=on]:text-white"
          >
            <Building2 className="h-3.5 w-3.5" />
            Companies
          </ToggleGroupItem>
        </ToggleGroup>

        <form onSubmit={handleSearch} className="space-y-3">
          <div className="space-y-1.5 max-w-md">
            <Label htmlFor="li-automation-keywords">Keywords</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                id="li-automation-keywords"
                placeholder={searchType === 'people' ? 'e.g. quality director' : 'e.g. pharmaceutical manufacturer'}
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
            <div className="space-y-1.5">
              <Label>Location</Label>
              <MultiSelectPopover
                value={selectedLocations}
                options={locationOptions}
                onChange={setSelectedLocations}
                placeholder="Any location"
                searchPlaceholder="Search locations..."
                showSelectAll
              />
            </div>
            <div className="space-y-1.5">
              <Label>Industry</Label>
              <MultiSelectPopover
                value={selectedIndustries}
                options={industryOptions}
                onChange={setSelectedIndustries}
                placeholder="Any industry"
                searchPlaceholder="Search industries..."
                showSelectAll
              />
            </div>
            {searchType === 'people' && (
              <div className="space-y-1.5">
                <Label>Degree</Label>
                <MultiSelectPopover
                  value={selectedDegrees}
                  options={degreeOptions}
                  onChange={setSelectedDegrees}
                  placeholder="Any degree"
                  searchPlaceholder="Search degrees..."
                  showSelectAll
                />
              </div>
            )}
          </div>

          {searchType === 'people' && presets && presets.presets.length > 0 && (
            <div className="flex flex-wrap gap-2 max-w-3xl">
              {presets.presets.map((preset) => (
                <Button
                  key={preset.name}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleApplyPreset(preset)}
                >
                  {preset.name}
                </Button>
              ))}
            </div>
          )}

          <Button type="submit" disabled={isSearching || !keywords.trim()}>
            {isSearching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            Search
          </Button>
        </form>
      </div>

      <div className="flex-1 p-6">
        {isSearching ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !hasSearched ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center space-y-2">
              <Linkedin className="h-8 w-8 mx-auto opacity-40" />
              <p>Enter keywords and search to find {searchType === 'people' ? 'people' : 'companies'} on LinkedIn</p>
            </div>
          </div>
        ) : searchType === 'people' ? (
          peopleResults && peopleResults.contacts.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center space-y-2">
                <Users className="h-8 w-8 mx-auto opacity-40" />
                <p>{peopleResults.totalResults} results</p>
                <p className="text-sm">{describeStoppedReason(peopleResults.stoppedReason)}</p>
              </div>
            </div>
          ) : (
            peopleResults && (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Profile</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {peopleResults.contacts.map((contact) => (
                      <TableRow key={contact.linkedinUrl}>
                        <TableCell className="font-medium">{contact.name}</TableCell>
                        <TableCell>{contact.title}</TableCell>
                        <TableCell>{contact.location}</TableCell>
                        <TableCell>
                          <a
                            href={contact.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-atek-blue hover:underline"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          )
        ) : companyResults && companyResults.companies.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center space-y-2">
              <Building2 className="h-8 w-8 mx-auto opacity-40" />
              <p>No companies found — try broadening keywords or removing filters.</p>
              <p className="text-sm">{companyResults.pagesSearched} pages searched</p>
            </div>
          </div>
        ) : (
          companyResults && (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Followers</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Profile</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companyResults.companies.map((company) => (
                    <TableRow key={company.linkedinUrl}>
                      <TableCell className="font-medium">{company.name}</TableCell>
                      <TableCell>{company.industry}</TableCell>
                      <TableCell>{company.location}</TableCell>
                      <TableCell>{company.followers}</TableCell>
                      <TableCell className="max-w-xs truncate">{company.description}</TableCell>
                      <TableCell>
                        <a
                          href={company.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-atek-blue hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        )}
      </div>
    </div>
  );
}
